// === WORKFLOW SUMMARY — per-product detail lines ===
// The summary card's per-product rows (name / coverage / premium / commission). Moved verbatim
// from WorkflowSummaryScreen.tsx; the only seams are the args (data / selectedProducts / config /
// t / packageDiscount) replacing the component closure, and the inner uiDiscountFactor duplicate
// now delegating to calc.ts's displayDiscountFactor (identical formula, identical outputs).
// Golden-tested offline in productDetails.test.ts.
import {
  computeAvpDisplayPremium,
  computeWoonhuisDisplayPremium,
  computePersonenautoDisplayPremium,
  computeRechtsbijstandDisplayPremium,
  formatNumber,
} from '@sluitstraat/core';
import { computeInboedelTotalFromSections } from '../../../utils/inboedelPricing';
import { getProductEntry } from '../../../products/registry';
import { getProductOrder } from '../../../workflowConfig';
import { displayDiscountFactor } from './calc';

// Product detail interface for type safety
export interface ProductDetail {
  id: string;
  name: string;
  coverage: string;
  premium: number;
  originalPremium?: number; // Premium before package discount
  icon: 'shield' | 'package' | 'house' | 'car' | 'scales';
  commissionAmount?: number;
}

// Get selected product details (using package-adjusted TTOT values when available)
// Display order follows the configured product order (getProductOrder — default: Woonhuis →
// Inboedel → AVP → Rechtsbijstand → Ongevallen → Personenauto → Doorlopende reis); the blocks
// below are collected in any order and sorted at the end.
export const computeProductDetails = (args: {
  data: any;
  selectedProducts: any;
  config: any;
  t: (key: string, opts?: any) => string;
  packageDiscount: { percentage: number; amount: number; newMonthlyPremium: number } | null;
}): ProductDetail[] => {
  const { data, selectedProducts, config, t, packageDiscount } = args;
  const products: ProductDetail[] = [];

  // Only show per-product original premium when an actual package discount exists
  const hasRealDiscount = packageDiscount !== null && packageDiscount.percentage > 0.01;

  // UI discount helpers: when applyToData is false, data is undiscounted so we apply discount at display time
  const whCfg = (config as any)?.woonhuisDiscount;
  const ibCfg = (config as any)?.inboedelDiscount;
  const whUiFactor = displayDiscountFactor(whCfg);
  const ibUiFactor = displayDiscountFactor(ibCfg);
  // AVP discount is baked into the data at calculation time, no UI overlay needed
  const avpUiFactor = 1;
  const rd = (v: number, f: number) => Math.round(v * f * 100) / 100;

  // Personenauto
  if (selectedProducts?.personenauto) {
    const personenautoData = data['personenauto'];

    // Per-product line uses the workflow data only — same pattern as Inboedel / Woonhuis
    // (see the Woonhuis comment below). Reading from packageProduct.PP re-runs the package
    // endpoint's numbers, which round CA_BTP slightly differently than the variant cache the
    // PersonenAutoScreen displays, creating a 1-cent gap between the front-page card and
    // the summary card. The package discount belongs on its own line in the total card.
    const originalPremium = computePersonenautoDisplayPremium(personenautoData, config as any);
    const premium = originalPremium;

    const commissionAmountRaw = personenautoData?.Contractdocument?.PP?.PP_TPP;

    const premiumNum = Number(premium);
    const originalPremiumNum = Number(originalPremium);
    const commissionAmount = commissionAmountRaw !== undefined ? Number(commissionAmountRaw) : undefined;

    products.push({
      id: 'personenauto',
      name: t('screens.offerSummary.productNames.personenauto'),
      coverage: '',
      premium: premiumNum,
      originalPremium: hasRealDiscount && originalPremiumNum !== premiumNum ? originalPremiumNum : undefined,
      icon: 'car',
      commissionAmount
    });
  }

  // Woonhuis
  if (selectedProducts?.woonhuis) {
    const woonhuisData = data['woonhuis'];
    // Per-product line uses the workflow data (already discounted by the WoonhuisScreen bake).
    // Reading from packageProduct.PP would also include the package-level discount, double-discounting
    // the line. The package discount belongs on its own line in the total card.
    const premium = rd(computeWoonhuisDisplayPremium(woonhuisData, config as any), whUiFactor);
    const originalPremium = premium;

    // Commission from workflow PP_TPP — matches the per-product premium source.
    const commissionAmountRaw = woonhuisData?.Contractdocument?.PP?.PP_TPP;

    const premiumNum = Number(premium);
    const originalPremiumNum = Number(originalPremium);
    const commissionAmount = commissionAmountRaw !== undefined ? Number(commissionAmountRaw) : undefined;

    const insuredSum = woonhuisData?.Contractdocument?.PP?.OP?.OP_VB || 0;
    products.push({
      id: 'woonhuis',
      name: t('screens.offerSummary.productNames.woonhuis'),
      coverage: Number(insuredSum) > 0 ? t('screens.offerSummary.coverageUpTo', { amount: formatNumber(insuredSum) }) : '',
      premium: premiumNum,
      originalPremium: hasRealDiscount && originalPremiumNum !== premiumNum ? originalPremiumNum : undefined,
      icon: 'house',
      commissionAmount
    });
  }

  // Inboedel
  if (selectedProducts?.inboedel) {
    const inboedelData = data['inboedel'];
    // Per-product line uses the workflow data (already discounted by the InboedelScreen bake).
    // Use the shared per-section helper so the displayed Inboedel total here matches the sum of
    // the badges shown on the InboedelScreen (e.g. base €14,24 + Glas €3,26 + ... = €23,99).
    // Calling computeInboedelDisplayPremium directly would single-floor the sum-of-BTPs, drifting
    // by 1-3¢ from the per-section view (would give €24,02 here).
    const premium = rd(computeInboedelTotalFromSections(inboedelData, config as any), ibUiFactor);
    const originalPremium = premium;

    // Get commission amount from PP_TPP — workflow data only (matches the per-product premium source).
    const commissionAmountRaw = inboedelData?.Contractdocument?.PP?.PP_TPP;

    const premiumNum = Number(premium);
    const originalPremiumNum = Number(originalPremium);
    const commissionAmount = commissionAmountRaw !== undefined ? Number(commissionAmountRaw) : undefined;

    const insuredSum = inboedelData?.Contractdocument?.PP?.IP?.IP_VB || 0;
    products.push({
      id: 'inboedel',
      name: t('screens.offerSummary.productNames.inboedel'),
      coverage: Number(insuredSum) > 0 ? t('screens.offerSummary.coverageUpTo', { amount: formatNumber(insuredSum) }) : '',
      premium: premiumNum,
      originalPremium: hasRealDiscount && originalPremiumNum !== premiumNum ? originalPremiumNum : undefined,
      icon: 'package',
      commissionAmount
    });
  }

  // Aansprakelijkheid (AVP)
  if (selectedProducts?.aansprakelijkheid) {
    const aansprakelijkheidData = data['aansprakelijkheid'];
    // Read from workflow data only — same pattern as woonhuis/inboedel above.
    // Workflow DA_BTP is kept at full precision (3.145) so the display math lands on €3.04.
    // The package response's DA_BTP is 2-decimal (we strip outbound at the BFF boundary),
    // which would round-trip drift to €3.03. AVP has no package-level discount anyway.
    const premium = rd(computeAvpDisplayPremium(aansprakelijkheidData, config as any), avpUiFactor);
    const originalPremium = premium;

    // Commission from workflow DA_TPP/PP_TPP — matches the per-product premium source.
    const commissionAmountRaw =
      aansprakelijkheidData?.Contractdocument?.PP?.PP_TPP ??
      aansprakelijkheidData?.Contractdocument?.PP?.DA?.DA_TPP;

    const premiumNum = Number(premium);
    const originalPremiumNum = Number(originalPremium);
    const commissionAmount = commissionAmountRaw !== undefined ? Number(commissionAmountRaw) : undefined;

    products.push({
      id: 'aansprakelijkheid',
      name: t('screens.offerSummary.productNames.aansprakelijkheid'),
      coverage: '',
      premium: premiumNum,
      originalPremium: hasRealDiscount && originalPremiumNum !== premiumNum ? originalPremiumNum : undefined,
      icon: 'shield',
      commissionAmount
    });
  }

  // Rechtsbijstand
  if (selectedProducts?.rechtsbijstand) {
    const rechtsbijstandData = data['rechtsbijstand'];
    const rbDekkingen = rechtsbijstandData?.Supportsection?.Dekkingen || {};

    // Single source of truth: sum-precise BTP × 0.8 × 1.21 → floor once, gated by dekkingen.
    // Same formula drives the Rechtsbijstand screen top badge, the breakdown net line × 1.21,
    // and the email — so every display point agrees on the cent.
    const originalPremium = computeRechtsbijstandDisplayPremium(rechtsbijstandData, config as any, rbDekkingen);
    const premium = originalPremium;

    const commissionAmountRaw = rechtsbijstandData?.Contractdocument?.PP?.PP_TPP;

    const premiumNum = Number(premium);
    const originalPremiumNum = Number(originalPremium);
    const commissionAmount = commissionAmountRaw !== undefined ? Number(commissionAmountRaw) : undefined;

    products.push({
      id: 'rechtsbijstand',
      name: t('screens.offerSummary.productNames.rechtsbijstand'),
      coverage: '',
      premium: premiumNum,
      originalPremium: hasRealDiscount && originalPremiumNum !== premiumNum ? originalPremiumNum : undefined,
      icon: 'scales',
      commissionAmount
    });
  }

  if (selectedProducts?.ongevallen) {
    const entry = getProductEntry('ongevallen');
    const premium = Number(entry?.premiumAccessor(data['ongevallen']) ?? 0) || 0;
    products.push({
      id: 'ongevallen',
      name: t(entry?.nameKey || 'products.ongevallen.name'),
      coverage: '',
      premium,
      icon: 'shield',
    });
  }

  if (selectedProducts?.doorlopendereis) {
    // DR has no UI discount — PP_TTOT (via the registry accessor) is the direct premium.
    const entry = getProductEntry('doorlopendereis');
    const premium = Number(entry?.premiumAccessor(data['doorlopendereis']) ?? 0) || 0;
    products.push({
      id: 'doorlopendereis',
      name: t(entry?.nameKey || 'products.doorlopendereis.name'),
      coverage: '',
      premium,
      icon: 'shield',
    });
  }

  const order = getProductOrder();
  products.sort((a, b) => order.indexOf(a.id as any) - order.indexOf(b.id as any));

  return products;
};
